import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DollarSign, FileText, Download, Check, X, RefreshCw, Clock, Eye, Mail, FileSpreadsheet, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatMoney } from '../lib/currency';
import { useCompanyScope } from '../context/CompanyScopeContext';

const CURRENCY = 'USD';
const fm = (v) => formatMoney(v, CURRENCY);

export default function PayrollAdmin() {
  const { selectedCompanyId, isCrossCompany } = useCompanyScope();
  const [employees, setEmployees] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [payslipStatus, setPayslipStatus] = useState({ generated: false, count: 0 });
  const [notifying, setNotifying] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [recalcProgress, setRecalcProgress] = useState(null); // null | { done, total, errors }

  // ── Prêts & Avances ──
  const [loans, setLoans] = useState([]);
  const [loanForm, setLoanForm] = useState({
    employee_id: '', loan_type: 'loan', amount: '', reason: '',
    start_month: '', installments_count: '1', installment_amount: ''
  });
  const [loanSubmitting, setLoanSubmitting] = useState(false);
  const [loanOpen, setLoanOpen] = useState(false);
  const [expandedLoan, setExpandedLoan] = useState(null);

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  // Période personnalisée (optionnelle)
  // Si non renseignée, on utilise le mois/année classique
  const [periodStart, setPeriodStart] = useState(''); // "YYYY-MM-DD"
  const [periodEnd, setPeriodEnd]     = useState(''); // "YYYY-MM-DD"

  // Recharger quand mois/année OU période change
  useEffect(() => { fetchAll(); }, [month, year]);

  const fetchStatus = async () => {
    try {
      const params = { month, year };
      if (periodStart) params.period_start = periodStart;
      const res = await api.get('/payslips/status', { params });
      setPayslipStatus(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const period = `${year}-${String(month).padStart(2, '0')}`;
      const statusParams = { month, year };
      if (periodStart) statusParams.period_start = periodStart;
      const [emp, ps, req, status, loansRes] = await Promise.all([
        api.get('/employees'),
        api.get('/payslips', { params: { period } }),
        api.get('/salary-requests'),
        api.get('/payslips/status', { params: statusParams }),
        api.get('/loans'),
      ]);
      setEmployees(emp.data);
      setPayslips(ps.data);
      setRequests(req.data);
      setPayslipStatus(status.data);
      setLoans(loansRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleGenerate = async (empId) => {
    setGenerating(empId);
    try {
      let url = `/payslips/generate?employee_id=${empId}&month=${month}&year=${year}`;
      if (periodStart && periodEnd) url += `&period_start=${periodStart}&period_end=${periodEnd}`;
      await api.post(url);
      toast.success('Fiche generee');
      const ps = await api.get('/payslips', { params: { period: `${year}-${String(month).padStart(2, '0')}` } });
      setPayslips(ps.data);
      await fetchStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setGenerating(null); }
  };

  const handleGenerateAll = async () => {
    if (payslipStatus.generated) return;
    setGenerating('all');
    let count = 0;
    const activeEmps = scopedEmployees.filter(e => e.status === 'active');
    for (const emp of activeEmps) {
      try {
        let url = `/payslips/generate?employee_id=${emp.id}&month=${month}&year=${year}`;
        if (periodStart && periodEnd) url += `&period_start=${periodStart}&period_end=${periodEnd}`;
        await api.post(url);
        count++;
      } catch (e) { console.error(e); }
    }
    toast.success(`✅ ${count} fiches de paie générées`);
    await fetchAll();
    setGenerating(null);
  };

  // ── Recalcul général (toujours disponible, écrase l'existant) ──
  const handleRecalculateAll = async () => {
    const companyLabel = isCrossCompany && selectedCompanyId
      ? scopedEmployees.find(e => e.company_id === selectedCompanyId)?.company_name || 'entreprise sélectionnée'
      : 'toutes les entreprises';
    if (!window.confirm(`Recalculer TOUTES les fiches de paie (${companyLabel}) pour ${months[month - 1]} ${year} ?\nLes fiches existantes seront écrasées.`)) return;
    setGenerating('recalc');
    setRecalcProgress({ done: 0, total: scopedEmployees.filter(e => e.status === 'active').length, errors: [] });
    try {
      let url = `/payslips/recalculate-all?month=${month}&year=${year}`;
      if (periodStart && periodEnd) url += `&period_start=${periodStart}&period_end=${periodEnd}`;
      // Passer le company_id sélectionné si filtre actif
      if (isCrossCompany && selectedCompanyId) url += `&company_id=${selectedCompanyId}`;
      const res = await api.post(url);
      const { generated, failed, errors, total } = res.data;
      setRecalcProgress({ done: total, total, errors: errors || [] });
      if (failed > 0) {
        toast.warning(`⚠️ ${generated}/${total} fiches recalculées — ${failed} erreur(s)`);
      } else {
        toast.success(`✅ ${generated} fiches recalculées avec succès`);
      }
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du recalcul');
    } finally {
      setGenerating(null);
      setTimeout(() => setRecalcProgress(null), 5000);
    }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const params = { month, year };
      // Passer le filtre entreprise si une entreprise est sélectionnée
      if (isCrossCompany && selectedCompanyId) {
        params.company_id = selectedCompanyId;
      }
      const res = await api.get('/payslips/export-excel', {
        params,
        responseType: 'blob'
      });
      // Nom du fichier avec l'entreprise si filtrée
      const companyName = isCrossCompany && selectedCompanyId
        ? (scopedEmployees.find(e => e.company_id === selectedCompanyId)?.company_name || selectedCompanyId)
        : null;
      const companySlug = companyName
        ? `_${companyName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()}`
        : '';
      const url = window.URL.createObjectURL(new Blob([res.data],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `journal_paie_${year}-${String(month).padStart(2,'0')}${companySlug}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Erreur export Excel'); }
    finally { setExportingExcel(false); }
  };

  const handleNotify = async () => {
    if (!window.confirm(`Envoyer les bulletins de paie par email à tous les employés pour ${months[month - 1]} ${year} ?`)) return;
    setNotifying(true);
    try {
      const res = await api.post('/payslips/notify', null, { params: { month, year } });
      toast.success(`📧 ${res.data.message}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur notification'); }
    finally { setNotifying(false); }
  };

  const handleDownloadPdf = async (payslipId, empName) => {
    try {
      const res = await api.get(`/payslips/${payslipId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `fiche_${empName.replace(/ /g,'_')}_${year}-${month}.pdf`; a.click();
    } catch (e) { toast.error('Erreur telechargemnt PDF'); }
  };

  const handlePreview = async (payslipId) => {
    setPreviewLoading(true);
    setPreviewHtml('loading');
    try {
      // Fetch via axios pour envoyer le token JWT en header (iframe ne peut pas)
      const res = await api.get(`/payslips/${payslipId}/preview`);
      setPreviewHtml(res.data);
    } catch (e) {
      toast.error('Impossible de charger l\'aperçu');
      setPreviewHtml(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApproveRequest = async (id) => {
    try { await api.post(`/salary-requests/${id}/approve`); toast.success('Approuve'); fetchAll(); } catch(e) { toast.error('Erreur'); }
  };
  const handleRejectRequest = async (id) => {
    try { await api.post(`/salary-requests/${id}/reject`); toast.success('Refuse'); fetchAll(); } catch(e) { toast.error('Erreur'); }
  };

  const getEmpName = (id) => { const e = employees.find(x => x.id === id); return e ? `${e.first_name} ${e.last_name}` : 'N/A'; };
  const typeLabels = { advance: 'Avance', credit: 'Crédit', loan: 'Prêt' };

  // Calcul automatique mensualité
  const calcInstallment = (amount, count) => {
    const a = parseFloat(amount), n = parseInt(count);
    if (a > 0 && n > 0) return (a / n).toFixed(2);
    return '';
  };

  const handleLoanFormChange = (field, value) => {
    setLoanForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'amount' || field === 'installments_count') {
        next.installment_amount = calcInstallment(
          field === 'amount' ? value : prev.amount,
          field === 'installments_count' ? value : prev.installments_count
        );
      }
      return next;
    });
  };

  const handleCreateLoan = async (e) => {
    e.preventDefault();
    if (!loanForm.employee_id || !loanForm.amount || !loanForm.start_month) {
      toast.error('Employé, montant et mois de début sont obligatoires');
      return;
    }
    setLoanSubmitting(true);
    try {
      await api.post('/loans', {
        employee_id:       loanForm.employee_id,
        loan_type:         loanForm.loan_type,
        amount:            parseFloat(loanForm.amount),
        reason:            loanForm.reason,
        start_month:       loanForm.start_month,
        installments_count: parseInt(loanForm.installments_count) || 1,
        installment_amount: loanForm.installment_amount ? parseFloat(loanForm.installment_amount) : null,
      });
      toast.success('✅ Prêt enregistré');
      setLoanOpen(false);
      setLoanForm({ employee_id: '', loan_type: 'loan', amount: '', reason: '', start_month: '', installments_count: '1', installment_amount: '' });
      const res = await api.get('/loans');
      setLoans(res.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setLoanSubmitting(false); }
  };

  const handleCancelLoan = async (loanId) => {
    if (!window.confirm('Annuler ce prêt ?')) return;
    try {
      await api.patch(`/loans/${loanId}/cancel`);
      toast.success('Prêt annulé');
      const res = await api.get('/loans');
      setLoans(res.data || []);
    } catch (err) { toast.error('Erreur'); }
  };

  const loanTypeColors = { loan: 'bg-violet-100 text-violet-700', advance: 'bg-amber-100 text-amber-700', credit: 'bg-blue-100 text-blue-700' };
  const loanStatusColors = { active: 'bg-green-100 text-green-700', completed: 'bg-slate-200 text-slate-600', cancelled: 'bg-red-100 text-red-600' };
  const loanStatusLabels = { active: 'Actif', completed: 'Terminé', cancelled: 'Annulé' };
  const months = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

  // Filtre par entreprise sélectionnée
  const scopedEmployees = useMemo(() => {
    if (!isCrossCompany || !selectedCompanyId) return employees;
    return employees.filter(e => e.company_id === selectedCompanyId);
  }, [employees, selectedCompanyId, isCrossCompany]);

  const scopedPayslips = useMemo(() => {
    if (!isCrossCompany || !selectedCompanyId) return payslips;
    return payslips.filter(p => p.company_id === selectedCompanyId);
  }, [payslips, selectedCompanyId, isCrossCompany]);

  const scopedRequests = useMemo(() => {
    if (!isCrossCompany || !selectedCompanyId) return requests;
    return requests.filter(r => r.company_id === selectedCompanyId);
  }, [requests, selectedCompanyId, isCrossCompany]);

  const pendingRequests = scopedRequests.filter(r => r.status === 'pending');

  if (loading) return <div className="text-center py-8">Chargement...</div>;

  return (
    <>
    <div className="space-y-6" data-testid="payroll-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Gestion de la Paie</h1>
          <p className="text-slate-600 mt-1">Fiches de paie, avances, credits et prets</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Sélecteur mois/année — pour filtrer l'affichage */}
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))} className="rounded-md border border-slate-300 p-2 text-sm" data-testid="payroll-month">
            {Array.from({length: 12}, (_, i) => <option key={i+1} value={i+1}>{['Jan','Fev','Mar','Avr','Mai','Juin','Juil','Aout','Sep','Oct','Nov','Dec'][i]}</option>)}
          </select>
          <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="w-24" data-testid="payroll-year" />
          <Button onClick={() => { setLoading(true); fetchAll(); }} variant="outline"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* ── Bloc période de paie personnalisée ── */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">
              📅 Période de paie
            </Label>
            <p className="text-xs text-slate-400 mb-2">Optionnel — si vide, utilise le 1er au dernier jour du mois sélectionné</p>
            <div className="flex items-center gap-2">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Du</Label>
                <Input
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  className="w-40 text-sm"
                  data-testid="period-start"
                />
              </div>
              <span className="text-slate-400 mt-4">→</span>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Au</Label>
                <Input
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  className="w-40 text-sm"
                  data-testid="period-end"
                />
              </div>
              {(periodStart || periodEnd) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-4 text-xs text-slate-400 hover:text-red-500"
                  onClick={() => { setPeriodStart(''); setPeriodEnd(''); }}
                >
                  ✕ Effacer
                </Button>
              )}
            </div>
          </div>
          {periodStart && periodEnd && (
            <div className="ml-auto flex items-center gap-2 rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-700">
              <Clock className="w-4 h-4" />
              Période : <strong>{new Date(periodStart).toLocaleDateString('fr-FR')} → {new Date(periodEnd).toLocaleDateString('fr-FR')}</strong>
              &nbsp;· Classé dans <strong>{months[month - 1]} {year}</strong>
            </div>
          )}
        </div>
      </div>

      <Tabs defaultValue="payslips" className="space-y-4">
        <TabsList>
          <TabsTrigger value="payslips" data-testid="tab-payslips">Fiches de paie</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">
            Demandes {pendingRequests.length > 0 && <Badge className="ml-1 bg-red-500 text-white text-xs">{pendingRequests.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="loans" data-testid="tab-loans">
            Prêts &amp; Avances {loans.filter(l => l.status === 'active').length > 0 && <Badge className="ml-1 bg-violet-500 text-white text-xs">{loans.filter(l => l.status === 'active').length}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="payslips">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3">
                {/* Titre + sélecteur */}
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Fiches de paie — {months[month - 1]} {year}</CardTitle>
                    {payslipStatus.generated && (
                      <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                        <Check className="w-3.5 h-3.5" />
                        {payslipStatus.count} fiche(s) générée(s) pour cette période
                      </p>
                    )}
                    {/* Barre de progression du recalcul */}
                    {recalcProgress && (
                      <div className="mt-2 w-72">
                        <div className="flex justify-between text-xs text-amber-700 mb-1">
                          <span>Recalcul en cours…</span>
                          <span>{recalcProgress.done}/{recalcProgress.total}</span>
                        </div>
                        <div className="h-2 bg-amber-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-amber-500 transition-all duration-300"
                            style={{ width: recalcProgress.total > 0 ? `${Math.round((recalcProgress.done / recalcProgress.total) * 100)}%` : '0%' }}
                          />
                        </div>
                        {recalcProgress.errors?.length > 0 && (
                          <p className="text-xs text-red-500 mt-1">{recalcProgress.errors.length} erreur(s)</p>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Boutons d'action */}
                  <div className="flex flex-wrap gap-2 justify-end">
                    {/* Générer — désactivé si déjà généré */}
                    <Button
                      onClick={handleGenerateAll}
                      disabled={generating === 'all' || generating === 'recalc' || payslipStatus.generated}
                      title={payslipStatus.generated ? 'Fiches déjà générées pour ce mois' : 'Générer toutes les fiches'}
                      data-testid="generate-all-btn"
                    >
                      {generating === 'all'
                        ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Génération...</>
                        : <><RefreshCw className="w-4 h-4 mr-2" />{payslipStatus.generated ? 'Fiches générées ✓' : 'Générer toutes les fiches'}</>
                      }
                    </Button>
                    {/* Recalcul général — toujours disponible */}
                    {payslipStatus.generated && (
                      <Button
                        onClick={handleRecalculateAll}
                        disabled={generating === 'recalc' || generating === 'all'}
                        variant="outline"
                        className="border-amber-400 text-amber-700 hover:bg-amber-50"
                        title="Recalculer toutes les fiches (écrase l'existant)"
                        data-testid="recalculate-all-btn"
                      >
                        {generating === 'recalc'
                          ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Recalcul...</>
                          : <><RefreshCw className="w-4 h-4 mr-2" />Recalcul général</>
                        }
                      </Button>
                    )}
                    {/* Export Excel */}
                    {payslipStatus.generated && (
                      <Button variant="outline" onClick={handleExportExcel} disabled={exportingExcel} data-testid="export-excel-btn">
                        {exportingExcel
                          ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Export...</>
                          : <><FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />Journal Excel</>
                        }
                      </Button>
                    )}
                    {/* Notifier */}
                    {payslipStatus.generated && (
                      <Button variant="outline" onClick={handleNotify} disabled={notifying} data-testid="notify-btn"
                        className="border-blue-300 text-blue-700 hover:bg-blue-50">
                        {notifying
                          ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Envoi...</>
                          : <><Bell className="w-4 h-4 mr-2" />Notifier les agents</>
                        }
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead>Employe</TableHead>
                      <TableHead>Période</TableHead>
                      <TableHead>Salaire Base</TableHead>
                      <TableHead>Brut</TableHead>
                      <TableHead>Retenues</TableHead>
                      <TableHead>Net</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scopedEmployees.filter(e => e.status === 'active').map(emp => {
                      const ps = scopedPayslips.find(p => p.employee_id === emp.id);
                      return (
                        <TableRow key={emp.id} data-testid={`payroll-row-${emp.id}`}>
                          <TableCell className="font-medium">{emp.first_name} {emp.last_name}</TableCell>
                          <TableCell className="text-xs text-slate-500">
                            {ps ? (
                              ps.period_start_display && ps.period_end_display
                                ? <span className="font-medium text-blue-700">{ps.period_start_display} → {ps.period_end_display}</span>
                                : <span>{months[(ps.month || month) - 1]} {ps.year || year}</span>
                            ) : (
                              periodStart && periodEnd
                                ? <span className="text-slate-400">{new Date(periodStart).toLocaleDateString('fr-FR')} → {new Date(periodEnd).toLocaleDateString('fr-FR')}</span>
                                : <span className="text-slate-400">{months[month - 1]} {year}</span>
                            )}
                          </TableCell>
                          <TableCell>{fm(emp.base_salary)}</TableCell>
                          <TableCell>{ps ? fm(ps.gross_salary) : '-'}</TableCell>
                          <TableCell>{ps ? fm(ps.total_deductions) : '-'}</TableCell>
                          <TableCell className="font-bold text-green-700">{ps ? fm(ps.net_pay) : '-'}</TableCell>
                          <TableCell>{ps ? <Badge className="bg-green-100 text-green-700" variant="outline">Genere</Badge> : <Badge variant="outline" className="text-slate-400">Non genere</Badge>}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="outline" onClick={() => handleGenerate(emp.id)} disabled={generating === emp.id} data-testid={`gen-${emp.id}`}>
                                <RefreshCw className={`w-3 h-3 mr-1 ${generating === emp.id ? 'animate-spin' : ''}`} /> {ps ? 'Recalculer' : 'Generer'}
                              </Button>
                              {ps && <>
                                <Button size="sm" variant="outline" onClick={() => handlePreview(ps.id)} data-testid={`preview-${emp.id}`} title="Aperçu du bulletin">
                                  <Eye className="w-3 h-3 mr-1" /> Aperçu
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(ps.id, `${emp.first_name}_${emp.last_name}`)} data-testid={`pdf-${emp.id}`}><Download className="w-3 h-3 mr-1" /> PDF</Button>
                              </>}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="requests">
          <Card>
            <CardHeader><CardTitle>Demandes d'avances, credits et prets</CardTitle></CardHeader>
            <CardContent>
              {scopedRequests.length === 0 ? (
                <p className="text-center text-slate-500 py-4">Aucune demande</p>
              ) : (
                <div className="space-y-3">
                  {scopedRequests.map(r => (
                    <div key={r.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border" data-testid={`admin-req-${r.id}`}>
                      <div className="flex items-center gap-3">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-medium">{r.employee_name} - <span className="text-blue-600">{typeLabels[r.request_type] || r.request_type}</span></p>
                          <p className="text-sm text-green-700 font-bold">{fm(r.amount)}</p>
                          <p className="text-xs text-slate-500">{r.message}</p>
                          {r.document_url && <a href={r.document_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600"><FileText className="w-3 h-3 inline mr-1" />{r.document_filename}</a>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.status === 'pending' ? (
                          <>
                            <Button size="sm" onClick={() => handleApproveRequest(r.id)} data-testid={`approve-req-${r.id}`}><Check className="w-3 h-3 mr-1" /> Approuver</Button>
                            <Button size="sm" variant="destructive" onClick={() => handleRejectRequest(r.id)} data-testid={`reject-req-${r.id}`}><X className="w-3 h-3" /></Button>
                          </>
                        ) : (
                          <Badge variant="outline" className={r.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {r.status === 'approved' ? 'Approuve' : 'Refuse'}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Onglet Prêts & Avances ── */}
        <TabsContent value="loans">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Prêts &amp; Avances sur salaire</CardTitle>
                <Button onClick={() => setLoanOpen(true)} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="create-loan-btn">
                  + Nouveau prêt / avance
                </Button>
              </div>
            </CardHeader>
            <CardContent>

              {/* Formulaire création */}
              {loanOpen && (
                <form onSubmit={handleCreateLoan} className="mb-6 rounded-xl border border-violet-200 bg-violet-50 p-5 space-y-4">
                  <h3 className="font-semibold text-violet-800 text-sm">Nouveau prêt / avance</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Employé *</Label>
                      <select
                        value={loanForm.employee_id}
                        onChange={e => handleLoanFormChange('employee_id', e.target.value)}
                        className="w-full rounded-md border border-slate-300 p-2 text-sm"
                        required data-testid="loan-employee"
                      >
                        <option value="">-- Choisir --</option>
                        {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Type *</Label>
                      <select
                        value={loanForm.loan_type}
                        onChange={e => handleLoanFormChange('loan_type', e.target.value)}
                        className="w-full rounded-md border border-slate-300 p-2 text-sm"
                        data-testid="loan-type"
                      >
                        <option value="loan">Prêt</option>
                        <option value="advance">Avance sur salaire</option>
                        <option value="credit">Crédit</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Montant total (USD) *</Label>
                      <Input type="number" step="0.01" min="0" value={loanForm.amount}
                        onChange={e => handleLoanFormChange('amount', e.target.value)}
                        placeholder="Ex: 500" required data-testid="loan-amount" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Nb mensualités</Label>
                      <Input type="number" min="1" max="60" value={loanForm.installments_count}
                        onChange={e => handleLoanFormChange('installments_count', e.target.value)}
                        placeholder="1 = déduit en une fois" data-testid="loan-installments" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Mensualité (USD)</Label>
                      <Input type="number" step="0.01" value={loanForm.installment_amount}
                        onChange={e => handleLoanFormChange('installment_amount', e.target.value)}
                        placeholder="Calculé auto" data-testid="loan-monthly" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">1er mois de remboursement *</Label>
                      <Input type="month" value={loanForm.start_month}
                        onChange={e => handleLoanFormChange('start_month', e.target.value)}
                        required data-testid="loan-start-month" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Motif</Label>
                      <Input value={loanForm.reason}
                        onChange={e => handleLoanFormChange('reason', e.target.value)}
                        placeholder="Ex: Avance sur salaire octobre" data-testid="loan-reason" />
                    </div>
                  </div>
                  {loanForm.amount && loanForm.installments_count && (
                    <div className="rounded-md bg-white border border-violet-200 px-4 py-2 text-sm text-violet-700">
                      📌 {loanForm.installments_count} échéance(s) de <strong>{loanForm.installment_amount || calcInstallment(loanForm.amount, loanForm.installments_count)} USD</strong>
                      {' '}débutant en <strong>{loanForm.start_month}</strong>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setLoanOpen(false)} className="flex-1">Annuler</Button>
                    <Button type="submit" disabled={loanSubmitting} className="flex-1 bg-violet-600 hover:bg-violet-700 text-white" data-testid="loan-submit">
                      {loanSubmitting ? 'Enregistrement...' : '✓ Enregistrer'}
                    </Button>
                  </div>
                </form>
              )}

              {/* Liste des prêts */}
              {loans.length === 0 ? (
                <p className="text-center text-slate-400 py-8">Aucun prêt enregistré</p>
              ) : (
                <div className="space-y-3">
                  {loans.map(loan => {
                    const paidAmt  = loan.installments?.filter(i => i.status === 'paid').reduce((s, i) => s + i.amount, 0) || 0;
                    const pct = loan.amount > 0 ? Math.round((paidAmt / loan.amount) * 100) : 0;
                    const isExpanded = expandedLoan === loan.id;
                    return (
                      <div key={loan.id} className="rounded-xl border bg-white shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between p-4">
                          <div className="flex items-center gap-3">
                            <div className="flex flex-col">
                              <span className="font-semibold text-slate-800">{loan.employee_name}</span>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${loanTypeColors[loan.loan_type] || 'bg-slate-100 text-slate-600'}`}>
                                  {typeLabels[loan.loan_type] || loan.loan_type}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${loanStatusColors[loan.status]}`}>
                                  {loanStatusLabels[loan.status]}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-slate-800">{fm(loan.amount)} USD</p>
                            <p className="text-xs text-slate-500">Restant : <span className="text-red-600 font-semibold">{fm(loan.remaining_amount || 0)}</span></p>
                          </div>
                        </div>

                        {/* Barre de progression */}
                        <div className="px-4 pb-2">
                          <div className="flex justify-between text-xs text-slate-500 mb-1">
                            <span>Remboursé : {fm(paidAmt)} USD</span>
                            <span>{pct}%</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-violet-500 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>

                        {/* Motif + échéances */}
                        <div className="px-4 pb-3">
                          {loan.reason && <p className="text-xs text-slate-500 mb-2">📝 {loan.reason}</p>}
                          <div className="flex gap-2">
                            <button
                              onClick={() => setExpandedLoan(isExpanded ? null : loan.id)}
                              className="text-xs text-violet-600 hover:underline"
                            >
                              {isExpanded ? '▲ Masquer les échéances' : '▼ Voir les échéances'} ({loan.installments?.length || 0})
                            </button>
                            {loan.status === 'active' && (
                              <button onClick={() => handleCancelLoan(loan.id)} className="text-xs text-red-500 hover:underline ml-auto" data-testid={`cancel-loan-${loan.id}`}>
                                Annuler le prêt
                              </button>
                            )}
                          </div>

                          {isExpanded && (
                            <div className="mt-3 rounded-lg border border-slate-100 overflow-hidden">
                              <table className="w-full text-xs">
                                <thead className="bg-slate-50">
                                  <tr>
                                    <th className="text-left px-3 py-2 text-slate-600">Mois</th>
                                    <th className="text-right px-3 py-2 text-slate-600">Montant</th>
                                    <th className="text-center px-3 py-2 text-slate-600">Statut</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {loan.installments?.map((inst, idx) => (
                                    <tr key={idx} className="border-t border-slate-50">
                                      <td className="px-3 py-1.5 text-slate-700">{inst.month}</td>
                                      <td className="px-3 py-1.5 text-right font-mono">{fm(inst.amount)}</td>
                                      <td className="px-3 py-1.5 text-center">
                                        {inst.status === 'paid'    && <span className="text-green-600 font-medium">✓ Payé</span>}
                                        {inst.status === 'pending' && <span className="text-amber-600">⏳ En attente</span>}
                                        {inst.status === 'skipped' && <span className="text-slate-400">Sauté</span>}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>

    {/* ── Modale Aperçu Bulletin ── */}
    {previewHtml && (
      <div className="fixed inset-0 z-50 flex flex-col bg-black/70">
        {/* Barre de contrôle */}
        <div className="flex items-center justify-between bg-slate-900 px-5 py-3 shrink-0">
          <span className="text-white font-semibold text-sm flex items-center gap-2">
            <Eye className="w-4 h-4 text-blue-400" />
            Aperçu — Bulletin de paie
          </span>
          <button
            onClick={() => setPreviewHtml(null)}
            className="text-slate-300 hover:text-white text-xl leading-none px-2"
            title="Fermer"
          >
            ✕
          </button>
        </div>
        {/* Contenu */}
        <div className="flex-1 overflow-hidden bg-gray-200">
          {previewHtml === 'loading' ? (
            <div className="flex items-center justify-center h-full">
              <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
          ) : (
            <iframe
              srcDoc={previewHtml}
              title="Aperçu bulletin de paie"
              className="w-full h-full border-0"
              style={{ background: '#eee' }}
            />
          )}
        </div>
      </div>
    )}
  </>
  );
}
